import { CanvasObjectInterface } from 'canvas/base-canvas-object';
import { CanvasRect } from 'canvas/canvas-rect/canvas-rect';
import { CanvasText } from 'canvas/canvas-text';
import { LayerInterface } from 'types/layer';
import { Layer } from 'layer';
import { CanvasRoundedRect } from 'canvas/canvas-rounded-rect/canvas-rounded-rect';
import { FlippedLayer } from 'flipped-layer/flipped-layer';

type SerializedLayer = {
	type: string;
	options: unknown;
	children: SerializedCanvasObject<unknown>[];
};

type SerializedFlippedLayer = {
	sides: Record<string, SerializedLayer>;
	type: string;
};

export type SerializedCanvasObject<T> = {
	type: string;
	options: T;
	minDimension: number;
};

export class Serializer {
	public static serialize(
		layer: LayerInterface
	): SerializedFlippedLayer | SerializedLayer | null {
		const layerType = layer.getType();

		if (layerType === 'layer') {
			return this.serializeSimpleLayer(layer);
		}

		if (layerType === 'flipped-layer') {
			return this.serializeFlippedLayer(layer as FlippedLayer);
		}

		return null;
	}

	public static serializeSimpleLayer(layer: LayerInterface): SerializedLayer {
		const data: SerializedLayer = {
			type: layer.getType(),
			options: layer.getOptions(),
			children: [],
		};
		for (const child of layer.getChildren()) {
			if (child as CanvasObjectInterface<unknown>) {
				data.children.push(this.serializeCanvasObject(child));
			}
		}
		return data;
	}

	public static serializeFlippedLayer(
		flippedLayer: FlippedLayer
	): SerializedFlippedLayer {
		const sides = flippedLayer.getSides();
		const data = {
			sides: {} as typeof sides,
			type: flippedLayer.getType(),
		};

		for (const side in sides) {
			if (sides[side]) {
				data.sides[side] = this.serializeSimpleLayer(sides[side]);
			}
		}

		return data;
	}

	private static serializeCanvasObject(
		canvasObject: CanvasObjectInterface<unknown>
	): SerializedCanvasObject<unknown> {
		const options = canvasObject.getOptions();

		return {
			type: canvasObject.getType(),
			options,
			minDimension: canvasObject.getMinDimension(),
		};
	}

	public static unserialize(
		serializedLayer: SerializedLayer | SerializedFlippedLayer
	): Layer | FlippedLayer | null {
		if (serializedLayer.type === 'layer') {
			return this.unserializeSimpleLayer(
				serializedLayer as SerializedLayer
			);
		}

		if (serializedLayer.type === 'flipped-layer') {
			return this.unserializeFlippedLayer(
				serializedLayer as SerializedFlippedLayer
			);
		}

		return null;
	}

	private static unserializeSimpleLayer(serializedLayer: SerializedLayer) {
		const layer = new Layer(serializedLayer.options as BaseDrawOptions);

		for (const child of serializedLayer.children) {
			if (child.type === 'rect') {
				const rect = new CanvasRect(child.options as RectDrawOptions);
				layer.addChild(rect);
			}

			if (child.type === 'text') {
				const text = new CanvasText(child.options as TextDrawOptions);
				layer.addChild(text);
			}
		}

		return layer;
	}

	private static unserializeFlippedLayer(
		serializedLayer: SerializedFlippedLayer
	) {
		const flippedLayer = new FlippedLayer();

		for (const side in serializedLayer.sides) {
			if (serializedLayer.sides[side]) {
				const layer = new Layer(
					serializedLayer.sides[side].options as BaseDrawOptions
				);

				for (const child of serializedLayer.sides[side].children) {
					if (child.type === 'rounded-rect') {
						const rect = new CanvasRoundedRect(
							child.options as RoundedRectDrawOptions
						);
						layer.addChild(rect);
					}

					if (child.type === 'text') {
						const text = new CanvasText(
							child.options as TextDrawOptions
						);
						layer.addChild(text);
					}
				}

				flippedLayer.add(side, layer);
			}
		}

		return flippedLayer;
	}
}
